import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {QlQueryDto} from "../../../../shared/ql/data/dto/ql-query.dto";
import {QlBuilderModel} from "../../../../shared/ql/builder/ql-builder-model";
import {QlBuilderQueryConverter} from "../../../../shared/ql/builder/ql-builder-query-converter";
import {QlBuilderViolation} from "../../../../shared/ql/builder/ql-builder-violation";
import {MvsMessageService} from "../../../../shared/service/message/mvs-message.service";
import {uiQlViolationText} from "../../../logic/ui-ql-violation-texts";
import {UiQlJsonDraft} from "../../../interface/ui-ql-json-draft.interface";
import {UiQlJsonImportResult} from "../../../interface/ui-ql-json-import-result.interface";

/**
 * Playground tab "QL-JSON": the generated `QlQueryDto` read-only with a copy button; the switch
 * "Bearbeiten" turns it into a textarea whose "JSON → Builder" re-imports the text through
 * `QlBuilderQueryConverter.fromQlQuery`. Unmappable parts and policy pre-check violations are shown
 * as errors and block the import — a definition is never partially or silently taken over.
 *
 * Declared in `UiQlWidgetBuilderModule` (NgModule, fe_core_standards) - its only host is
 * `UiQlWidgetWorkbenchPage` of the same module.
 */
@Component({
    selector: 'ui-ql-json-view',
    templateUrl: './ui-ql-json-view.component.html',
    styleUrls: ['./ui-ql-json-view.component.scss'],
    standalone: false,
})
export class UiQlJsonViewComponent implements OnChanges {

    /** The definition generated from the builder model (`state.qlQuery$`). */
    @Input() qlQuery: QlQueryDto | null = null;

    /** Raw text to open in the editor (e.g. preset #8, which the builder model cannot represent). */
    @Input() draft: UiQlJsonDraft | null = null;

    /** A successfully converted model — the host calls `state.setModel`. */
    @Output() applyModel = new EventEmitter<QlBuilderModel>();

    json: string = '';
    editing: boolean = false;
    editText: string = '';
    result: UiQlJsonImportResult | null = null;
    applied: boolean = false;

    /**
     * The definition changed (builder edit, preset, chat) while the editor holds a text the user has changed
     * (QA #6): the text is kept, this flag shows "Definition hat sich geändert – Text neu laden".
     */
    staleDefinition: boolean = false;

    private lastDraftSeq: number | null = null;

    /** The text the editor was last filled from - `editText === editSourceText` means "not edited by the user". */
    private editSourceText: string = '';

    /** After "JSON → Builder" the next `qlQuery` change is the echo of that very import: resync silently. */
    private expectApplyEcho: boolean = false;

    constructor(private readonly messageService: MvsMessageService) {
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (changes['qlQuery']) {
            this.json = this.qlQuery ? JSON.stringify(this.qlQuery, null, 2) : '';
            if (this.editing) {
                this.onDefinitionChangedWhileEditing();
            }
        }

        if (changes['draft'] && this.draft && this.draft.seq !== this.lastDraftSeq) {
            this.lastDraftSeq = this.draft.seq;
            this.editing = true;
            this.fillEditor(this.draft.text);
            this.result = UiQlJsonViewComponent.importJson(this.editText);
        }
    }

    onEditingChange(editing: boolean): void {
        this.editing = !!editing;
        this.applied = false;
        this.result = null;
        this.staleDefinition = false;
        if (this.editing) {
            this.fillEditor(this.json);
        }
    }

    /** "Text neu laden": replace the user's text with the current definition. */
    reloadText(): void {
        this.fillEditor(this.json);
        this.result = null;
        this.applied = false;
    }

    /** `true` while the editor text differs from what it was filled with. */
    get editedByUser(): boolean {
        return this.editText !== this.editSourceText;
    }

    private fillEditor(text: string): void {
        this.editText = text ?? '';
        this.editSourceText = this.editText;
        this.staleDefinition = false;
        this.expectApplyEcho = false;
    }

    /**
     * A new `qlQuery` in edit mode (QA #6): an untouched editor (or the echo of its own import) follows the
     * definition; a text the user changed is kept and flagged stale instead of being overwritten.
     */
    private onDefinitionChangedWhileEditing(): void {
        if (this.expectApplyEcho || !this.editedByUser) {
            const keepApplied = this.expectApplyEcho && this.applied;
            this.fillEditor(this.json);
            this.applied = keepApplied;
            if (!keepApplied) {
                this.result = null;
            }
            return;
        }
        this.staleDefinition = true;
    }

    onEditTextChange(text: string): void {
        this.editText = text ?? '';
        this.applied = false;
        this.result = null;
    }

    /** "JSON → Builder". */
    applyJson(): void {
        this.result = UiQlJsonViewComponent.importJson(this.editText);
        this.applied = false;
        if (this.result.model) {
            this.applied = true;
            this.staleDefinition = false;
            this.expectApplyEcho = true;
            this.applyModel.emit(this.result.model);
        }
    }

    copy(): void {
        const text = this.editing ? this.editText : this.json;
        if (!text) {
            return;
        }
        const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
        if (!clipboard?.writeText) {
            this.messageService.showWarningMessage('Kopieren nicht möglich', 'Die Zwischenablage ist in diesem Browser nicht verfügbar.');
            return;
        }
        clipboard.writeText(text).then(
            () => this.messageService.showSuccessMessage('Kopiert', 'Die QL-Definition liegt in der Zwischenablage.'),
            () => this.messageService.showErrorMessage('Kopieren fehlgeschlagen', 'Die QL-Definition konnte nicht kopiert werden.'),
        );
    }

    violationText(violation: QlBuilderViolation): string {
        return uiQlViolationText(violation);
    }

    /**
     * Parse + convert + pre-check one JSON text. Pure; the only place that decides whether a text
     * may reach the builder: a model comes back ONLY when the text parses, converts without any
     * unmappable part and passes the client policy pre-check.
     */
    static importJson(text: string): UiQlJsonImportResult {

        let parsed: unknown;
        try {
            parsed = JSON.parse(text ?? '');
        } catch (error) {
            return {
                model: null,
                parseError: 'Ungültiges JSON: Der Text lässt sich nicht als JSON lesen. Bitte Klammern, Anführungszeichen und Kommas prüfen.',
                parseErrorDetail: error instanceof Error ? error.message : null,
                unmappable: [],
                violations: [],
            };
        }

        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return {model: null, parseError: 'Erwartet wird ein einzelnes QL-Abfrageobjekt ({ … }).', unmappable: [], violations: []};
        }

        const dto = parsed as QlQueryDto;
        const violations = QlBuilderQueryConverter.precheckQlQuery(dto);

        let converted: { model: QlBuilderModel | null; unmappable: string[] };
        try {
            converted = QlBuilderQueryConverter.fromQlQuery(dto);
        } catch (error) {
            converted = {model: null, unmappable: ['Die Definition konnte nicht gelesen werden (unerwartete Struktur).']};
        }

        const unmappable = converted.unmappable ?? [];
        const ok = !!converted.model && !unmappable.length && !violations.length;

        return {model: ok ? converted.model : null, parseError: null, unmappable, violations};
    }
}
